// ============================================
// VERSION 41 PRODUCTION - CLEAN & OPTIMIZED
// Rule: Filters with selections CANNOT close
// Fix: Pointer events + dual-phase click handling
// ============================================

if (typeof debounce === 'undefined') {
  function debounce(fn, wait) {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn.apply(this, args), wait);
    };
  }
}

class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 500);

    const facetForm = this.querySelector('form');
    if (facetForm) {
      const inputs = facetForm.querySelectorAll('input[type="checkbox"], input[type="text"], input[type="number"], select');
      
      inputs.forEach((input) => {
        input.addEventListener('change', (e) => {
          const filterDetails = e.target.closest('.js-filter');
          if (filterDetails && !filterDetails.hasAttribute('open')) {
            filterDetails.setAttribute('open', '');
          }
        });
        
        input.addEventListener('input', this.debouncedOnSubmit.bind(this));
        input.addEventListener('change', this.debouncedOnSubmit.bind(this));
      });
    }

    const facetWrapper = this.querySelector('#FacetsWrapperDesktop');
    if (facetWrapper && typeof onKeyUpEscape === 'function') {
      facetWrapper.addEventListener('keyup', onKeyUpEscape);
    }

    this.attachSummaryHandlers();
    this.startMutationObserver();
  }

  attachSummaryHandlers() {
    this.querySelectorAll('.js-filter').forEach((details) => {
      const summary = details.querySelector('summary');
      if (!summary) return;
      
      // Clone summary to remove all existing event listeners
      const newSummary = summary.cloneNode(true);
      summary.parentNode.replaceChild(newSummary, summary);
      newSummary.dataset.facetHandlerAttached = 'true';
      
      let isProcessing = false;
      
      // CAPTURE PHASE: Block global.js for clicks inside summary bounds
      newSummary.addEventListener('click', (e) => {
        const clickY = e.clientY;
        const summaryRect = newSummary.getBoundingClientRect();
        const isClickInsideSummaryBounds = 
          clickY >= summaryRect.top && 
          clickY <= summaryRect.bottom &&
          e.clientX >= summaryRect.left && 
          e.clientX <= summaryRect.right;
        
        const facetsDisplay = details.querySelector('.facets__display, .facets__display-vertical, .mobile-facets__list');
        const isClickOnFilterContent = facetsDisplay && facetsDisplay.contains(e.target);
        
        // IMPORTANT: Don't interfere with clicks outside the filter area
        const isClickOnFilter = details.contains(e.target);
        if (!isClickOnFilter) {
          return; // Click is outside this filter entirely, don't touch it
        }
        
        // Let filter content clicks through completely
        if (isClickOnFilterContent || (!isClickInsideSummaryBounds && clickY > summaryRect.bottom)) {
          return;
        }
        
        // Block global.js for summary clicks
        e.stopPropagation();
        e.stopImmediatePropagation();
      }, true);
      
      // BUBBLE PHASE: Handle toggle logic
      newSummary.addEventListener('click', (e) => {
        // CRITICAL: Only handle clicks that originated from within this filter
        if (!details.contains(e.target)) {
          return; // Click is outside this filter, don't interfere
        }
        
        if (isProcessing) {
          e.preventDefault();
          e.stopPropagation();
          e.stopImmediatePropagation();
          return;
        }
        
        const facetsDisplay = details.querySelector('.facets__display, .facets__display-vertical, .mobile-facets__list');
        const isClickOnFilterContent = facetsDisplay && facetsDisplay.contains(e.target);
        const clickY = e.clientY;
        const summaryRect = newSummary.getBoundingClientRect();
        const isClickBelowSummary = clickY > summaryRect.bottom;
        
        // Let filter content clicks through (but prevent native toggle)
        if (isClickOnFilterContent || isClickBelowSummary) {
          e.preventDefault();
          return;
        }
        
        isProcessing = true;
        setTimeout(() => { isProcessing = false; }, 100);
        
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        
        const isOpen = details.hasAttribute('open');
        const hasChecked = details.querySelector('input[type="checkbox"]:checked');
        
        // Never close filters with selections
        if (isOpen && hasChecked) {
          return;
        }
        
        // Toggle filter
        if (isOpen) {
          details.removeAttribute('open');
          newSummary.setAttribute('aria-expanded', 'false');
        } else {
          details.setAttribute('open', '');
          newSummary.setAttribute('aria-expanded', 'true');
        }
      });
    });
  }

  startMutationObserver() {
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' && mutation.attributeName === 'open') {
          const filter = mutation.target;
          const isOpen = filter.hasAttribute('open');
          const hasChecked = filter.querySelector('input[type="checkbox"]:checked');
          
          // Force restore filters with selections
          if (hasChecked && !isOpen) {
            observer.disconnect();
            filter.setAttribute('open', '');
            const sum = filter.querySelector('summary');
            if (sum) sum.setAttribute('aria-expanded', 'true');
            
            setTimeout(() => {
              document.querySelectorAll('.js-filter').forEach((f) => {
                observer.observe(f, { attributes: true, attributeFilter: ['open'] });
              });
            }, 10);
          }
        }
      });
    });

    this.querySelectorAll('.js-filter').forEach((filter) => {
      observer.observe(filter, { attributes: true, attributeFilter: ['open'] });
    });

    this.filterObserver = observer;
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state ? event.state.searchParams : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    };
    window.addEventListener('popstate', onHistoryChange);
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    FacetFiltersForm.searchParamsPrev = searchParams;

    const sections = FacetFiltersForm.getSections();
    const countContainer = document.getElementById('ProductCount');
    const countContainerDesktop = document.getElementById('ProductCountDesktop');
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
    );
    loadingSpinners.forEach((spinner) => spinner.classList.remove('hidden'));

    const productGridContainer = document.getElementById('ProductGridContainer');
    if (productGridContainer) {
      const collectionElement = productGridContainer.querySelector('.collection');
      if (collectionElement) collectionElement.classList.add('loading');
    }
    if (countContainer) countContainer.classList.add('loading');
    if (countContainerDesktop) countContainerDesktop.classList.add('loading');

    if (sections.length > 0) {
      sections.forEach((section) => {
        const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
        const filterDataUrl = (element) => element.url === url;

        FacetFiltersForm.filterData.some(filterDataUrl)
          ? FacetFiltersForm.renderSectionFromCache(filterDataUrl, event)
          : FacetFiltersForm.renderSectionFromFetch(url, event);
      });
    } else {
      const url = `${window.location.pathname}?${searchParams}`;
      FacetFiltersForm.renderFullPageFromFetch(url, event);
    }

    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
  }

  static renderSectionFromFetch(url, event) {
    fetch(url)
      .then((response) => response.text())
      .then((responseText) => {
        const html = responseText;
        FacetFiltersForm.filterData = [...FacetFiltersForm.filterData, { html, url }];
        FacetFiltersForm.renderFilters(html, event);
        FacetFiltersForm.renderProductGridContainer(html);
        FacetFiltersForm.renderProductCount(html);

        if (typeof initializeScrollAnimationTrigger === 'function') {
          try { 
            initializeScrollAnimationTrigger(document); 
          } catch (e) { 
            console.warn('initializeScrollAnimationTrigger error:', e); 
          }
        }
      });
  }

  static renderFullPageFromFetch(url, event) {
    const currentlyOpenFilters = [];
    document.querySelectorAll('.js-filter[open]').forEach((filter) => {
      currentlyOpenFilters.push(filter.id);
    });

    fetch(url)
      .then((response) => response.text())
      .then((responseText) => {
        const parser = new DOMParser();
        const doc = parser.parseFromString(responseText, 'text/html');

        const newFilters = doc.querySelector('#FacetsWrapperDesktop, .facets__wrapper');
        const currentFilters = document.querySelector('#FacetsWrapperDesktop, .facets__wrapper');

        if (newFilters && currentFilters) {
          const newFilterElements = newFilters.querySelectorAll('.js-filter');

          newFilterElements.forEach((newFilter) => {
            const currentFilter = document.getElementById(newFilter.id);
            if (!currentFilter) return;

            const wasOpenBefore = currentlyOpenFilters.includes(currentFilter.id);

            const currentDisplay = currentFilter.querySelector('.facets__display, .facets__display-vertical');
            const newDisplay = newFilter.querySelector('.facets__display, .facets__display-vertical');
            if (currentDisplay && newDisplay) {
              currentDisplay.innerHTML = newDisplay.innerHTML;
            }

            const currentSummary = currentFilter.querySelector('.facets__summary');
            const newSummary = newFilter.querySelector('.facets__summary');
            if (currentSummary && newSummary) {
              const currentSelected = currentSummary.querySelector('.facets__selected');
              const newSelected = newSummary.querySelector('.facets__selected');
              if (currentSelected && newSelected) {
                currentSelected.innerHTML = newSelected.innerHTML;
              }
            }

            const formEl = document.querySelector('facet-filters-form');
            if (formEl && formEl.debouncedOnSubmit) {
              const newInputs = currentFilter.querySelectorAll('input[type="checkbox"], input[type="text"], input[type="number"], select');
              
              newInputs.forEach((input) => {
                const newInput = input.cloneNode(true);
                input.parentNode.replaceChild(newInput, input);
                
                newInput.addEventListener('change', (e) => {
                  const filterDetails = e.target.closest('.js-filter');
                  if (filterDetails && !filterDetails.hasAttribute('open')) {
                    filterDetails.setAttribute('open', '');
                  }
                });
                
                newInput.addEventListener('input', formEl.debouncedOnSubmit.bind(formEl));
                newInput.addEventListener('change', formEl.debouncedOnSubmit.bind(formEl));
              });
            }

            const hasCheckedItems = currentFilter.querySelector('input[type="checkbox"]:checked');
            if (wasOpenBefore || hasCheckedItems) {
              currentFilter.setAttribute('open', '');
            } else {
              currentFilter.removeAttribute('open');
            }
          });
        }

        const newGrid = doc.getElementById('collection-products') || doc.getElementById('ProductGridContainer');
        const currentGrid = document.getElementById('collection-products') || document.getElementById('ProductGridContainer');

        if (newGrid && currentGrid) {
          currentGrid.innerHTML = newGrid.innerHTML;
          currentGrid.querySelectorAll('.scroll-trigger').forEach((el) => {
            el.classList.add('scroll-trigger--cancel');
          });
        }

        setTimeout(() => {
          document.querySelectorAll('.js-filter').forEach(filter => {
            const hasChecked = filter.querySelector('input[type="checkbox"]:checked');
            if (hasChecked) {
              filter.setAttribute('open', '');
            }
          });
          
          const formEl = document.querySelector('facet-filters-form');
          if (formEl) {
            formEl.attachSummaryHandlers();
            formEl.startMutationObserver();
          }
          
          // Reattach load-more button AFTER HTML replacement
          FacetFiltersForm.reattachLoadMoreButton();
        }, 50);

        if (typeof initializeScrollAnimationTrigger === 'function') {
          try {
            initializeScrollAnimationTrigger(responseText);
          } catch(e) {
            console.warn('initializeScrollAnimationTrigger error:', e);
          }
        }
      })
      .catch(err => console.error('Error fetching page:', err));
  }

  static renderSectionFromCache(filterDataUrl, event) {
    const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
    FacetFiltersForm.renderFilters(html, event);
    FacetFiltersForm.renderProductGridContainer(html);
    FacetFiltersForm.renderProductCount(html);

    if (typeof initializeScrollAnimationTrigger === 'function') {
      try { 
        initializeScrollAnimationTrigger(document); 
      } catch (e) { 
        console.warn('initializeScrollAnimationTrigger error:', e); 
      }
    }
  }

  static renderProductGridContainer(html) {
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const newGrid = parsed.getElementById('ProductGridContainer');
    if (!newGrid) return;
    const container = document.getElementById('ProductGridContainer');
    if (!container) return;

    container.innerHTML = newGrid.innerHTML;
    container.querySelectorAll('.scroll-trigger').forEach((el) => {
      el.classList.add('scroll-trigger--cancel');
    });
    
    // Reattach load-more button handler if it exists
    FacetFiltersForm.reattachLoadMoreButton();
  }

  static renderProductCount(html) {
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const countEl = parsed.getElementById('ProductCount');
    if (!countEl) return;

    const container = document.getElementById('ProductCount');
    const containerDesktop = document.getElementById('ProductCountDesktop');
    if (container) {
      container.innerHTML = countEl.innerHTML;
      container.classList.remove('loading');
    }
    if (containerDesktop) {
      containerDesktop.innerHTML = countEl.innerHTML;
      containerDesktop.classList.remove('loading');
    }

    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
    );
    loadingSpinners.forEach((spinner) => spinner.classList.add('hidden'));
  }

  static renderFilters(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
    const facetDetailsElementsFromFetch = parsedHTML.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
    );
    const facetDetailsElementsFromDom = document.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
    );

    const currentlyOpenFilterIds = [];
    facetDetailsElementsFromDom.forEach(el => {
      if (el.hasAttribute('open')) {
        currentlyOpenFilterIds.push(el.id);
      }
    });

    Array.from(facetDetailsElementsFromDom).forEach((currentElement) => {
      if (!Array.from(facetDetailsElementsFromFetch).some(({ id }) => currentElement.id === id)) {
        currentElement.remove();
      }
    });

    const matchesId = (element) => {
      const jsFilter = event ? event.target.closest('.js-filter') : undefined;
      return jsFilter ? element.id === jsFilter.id : false;
    };

    const facetsToRender = Array.from(facetDetailsElementsFromFetch).filter((element) => !matchesId(element));
    const countsToRender = Array.from(facetDetailsElementsFromFetch).find(matchesId);

    facetsToRender.forEach((elementToRender, index) => {
      const currentElement = document.getElementById(elementToRender.id);

      if (currentElement) {
        const wasOpenBefore = currentlyOpenFilterIds.includes(currentElement.id);

        const currentDisplay = currentElement.querySelector('.facets__display, .facets__display-vertical');
        const newDisplay = elementToRender.querySelector('.facets__display, .facets__display-vertical');
        if (currentDisplay && newDisplay) {
          currentDisplay.innerHTML = newDisplay.innerHTML;
        }

        const currentSummary = currentElement.querySelector('.facets__summary');
        const newSummary = elementToRender.querySelector('.facets__summary');
        if (currentSummary && newSummary) {
          const currentSelectedSpan = currentSummary.querySelector('.facets__selected');
          const newSelectedSpan = newSummary.querySelector('.facets__selected');
          if (currentSelectedSpan && newSelectedSpan) {
            currentSelectedSpan.innerHTML = newSelectedSpan.innerHTML;
          }
        }

        const form = document.querySelector('facet-filters-form');
        if (form && form.debouncedOnSubmit) {
          const newInputs = currentElement.querySelectorAll('input[type="checkbox"], input[type="text"], input[type="number"], select');
          
          newInputs.forEach((input) => {
            const newInput = input.cloneNode(true);
            input.parentNode.replaceChild(newInput, input);
            
            newInput.addEventListener('change', (e) => {
              const filterDetails = e.target.closest('.js-filter');
              if (filterDetails && !filterDetails.hasAttribute('open')) {
                filterDetails.setAttribute('open', '');
              }
            });
            
            newInput.addEventListener('input', form.debouncedOnSubmit.bind(form));
            newInput.addEventListener('change', form.debouncedOnSubmit.bind(form));
          });
        }

        const hasCheckedItems = currentElement.querySelector('input[type="checkbox"]:checked');
        if (wasOpenBefore || hasCheckedItems) {
          currentElement.setAttribute('open', '');
        } else {
          currentElement.removeAttribute('open');
        }
      } else {
        if (index > 0) {
          const { className: previousElementClassName, id: previousElementId } = facetsToRender[index - 1];
          if (elementToRender.className === previousElementClassName) {
            document.getElementById(previousElementId).after(elementToRender);
            return;
          }
        }
        if (elementToRender.parentElement) {
          document.querySelector(`#${elementToRender.parentElement.id} .js-filter`).before(elementToRender);
        }
      }
    });

    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);

    setTimeout(() => {
      document.querySelectorAll('.js-filter').forEach(filter => {
        const hasChecked = filter.querySelector('input[type="checkbox"]:checked');
        if (hasChecked) {
          filter.setAttribute('open', '');
        }
      });
      
      const formEl = document.querySelector('facet-filters-form');
      if (formEl) {
        formEl.attachSummaryHandlers();
        formEl.startMutationObserver();
      }
    }, 50);

    if (countsToRender) {
      const closestJSFilterID = event?.target?.closest('.js-filter')?.id;
      if (closestJSFilterID) {
        FacetFiltersForm.renderCounts(countsToRender, document.getElementById(closestJSFilterID));
        FacetFiltersForm.renderMobileCounts(countsToRender, document.getElementById(closestJSFilterID));

        const newFacetDetailsElement = document.getElementById(closestJSFilterID);
        const newElementSelector = newFacetDetailsElement.classList.contains('mobile-facets__details')
          ? `.mobile-facets__close-button`
          : `.facets__summary`;
        const newElementToActivate = newFacetDetailsElement.querySelector(newElementSelector);

        const isTextInput = event.target.getAttribute('type') === 'text';
        if (newElementToActivate && !isTextInput) newElementToActivate.focus();
      }
    }
  }

  static renderActiveFacets(html) {
    const activeFacetElementSelectors = ['.active-facets-mobile', '.active-facets-desktop'];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      const domEl = document.querySelector(selector);
      if (!activeFacetsElement || !domEl) return;
      domEl.innerHTML = activeFacetsElement.innerHTML;
    });

    FacetFiltersForm.toggleActiveFacets(false);
  }

  static renderAdditionalElements(html) {
    const mobileElementSelectors = ['.mobile-facets__open', '.mobile-facets__count', '.sorting'];

    mobileElementSelectors.forEach((selector) => {
      const htmlElement = html.querySelector(selector);
      const domElement = document.querySelector(selector);
      if (!htmlElement || !domElement) return;
      domElement.innerHTML = htmlElement.innerHTML;
    });

    const mobileForm = document.getElementById('FacetFiltersFormMobile');
    if (mobileForm) {
      const menuDrawer = mobileForm.closest('menu-drawer');
      if (menuDrawer && typeof menuDrawer.bindEvents === 'function') menuDrawer.bindEvents();
    }
  }

  static renderCounts(source, target) {
    if (!source || !target) return;

    const targetSummary = target.querySelector('.facets__summary');
    const sourceSummary = source.querySelector('.facets__summary');
    if (sourceSummary && targetSummary) targetSummary.outerHTML = sourceSummary.outerHTML;

    const targetHeaderElement = target.querySelector('.facets__header');
    const sourceHeaderElement = source.querySelector('.facets__header');
    if (sourceHeaderElement && targetHeaderElement) targetHeaderElement.outerHTML = sourceHeaderElement.outerHTML;

    const targetWrapElement = target.querySelector('.facets-wrap');
    const sourceWrapElement = source.querySelector('.facets-wrap');
    if (sourceWrapElement && targetWrapElement) {
      const isShowingMore = Boolean(target.querySelector('show-more-button .label-show-more.hidden'));
      if (isShowingMore) {
        sourceWrapElement
          .querySelectorAll('.facets__item.hidden')
          .forEach((hiddenItem) => hiddenItem.classList.replace('hidden', 'show-more-item'));
      }
      targetWrapElement.outerHTML = sourceWrapElement.outerHTML;
    }
  }

  static renderMobileCounts(source, target) {
    if (!source || !target) return;
    const targetFacetsList = target.querySelector('.mobile-facets__list');
    const sourceFacetsList = source.querySelector('.mobile-facets__list');
    if (sourceFacetsList && targetFacetsList) targetFacetsList.outerHTML = sourceFacetsList.outerHTML;
  }

  static updateURLHash(searchParams) {
    history.pushState({ searchParams }, '', `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`);
  }

  static getSections() {
    let grid = document.getElementById('product-grid');
    let sectionId = grid?.dataset?.id;

    if (!sectionId) {
      grid = document.getElementById('ProductGridContainer');
      sectionId = grid?.dataset?.id;
    }

    if (sectionId) return [{ section: sectionId }];

    if (!FacetFiltersForm._warnedNoSection) {
      console.warn('No product-grid with data-id found, using full page fetch');
      FacetFiltersForm._warnedNoSection = true;
    }
    return [];
  }
  
  static reattachLoadMoreButton() {
    setTimeout(() => {
      const oldBtn = document.getElementById('paginateNext');
      if (!oldBtn) return;
      
      // Get necessary data
      const nextUrl = oldBtn.getAttribute('data-next-url');
      const btnClass = oldBtn.className;
      const btnText = oldBtn.textContent.trim();
      
      // Initialize global variables
      const spinner = document.querySelector('.load-more_spinner');
      const productGrid = document.getElementById('product-grid');
      
      window.load_more_spinner = spinner;
      window.products_on_page = productGrid;
      window.next_url = nextUrl;
      
      // Create fresh button
      const newBtn = document.createElement('a');
      newBtn.id = 'paginateNext';
      newBtn.href = '#';
      newBtn.className = btnClass;
      newBtn.textContent = btnText;
      newBtn.setAttribute('data-next-url', nextUrl);
      
      // Replace broken loadMoreProducts with working version
      window.loadMoreProducts = async function() {
        if (!window.next_url) return;
        
        if (window.load_more_btn) window.load_more_btn.style.display = 'none';
        if (window.load_more_spinner) window.load_more_spinner.classList.remove('hidden');
        
        try {
          const response = await fetch(window.next_url);
          const html = await response.text();
          const doc = new DOMParser().parseFromString(html, 'text/html');
          
          if (window.load_more_spinner) window.load_more_spinner.classList.add('hidden');
          
          const productgrid = doc.getElementById('product-grid');
          if (!productgrid) return;
          
          const new_products = Array.from(productgrid.getElementsByClassName('grid__item'));
          const newPagination = doc.getElementById('paginateNext');
          const new_url = newPagination ? newPagination.dataset.nextUrl : '';
          
          if (window.load_more_btn) {
            window.load_more_btn.style.display = new_url ? 'flex' : 'none';
          }
          window.next_url = new_url;
          
          for (let i = 0; i < new_products.length; i++) {
            window.products_on_page.appendChild(new_products[i]);
          }
        } catch (error) {
          console.error('Load more error:', error);
          if (window.load_more_spinner) window.load_more_spinner.classList.add('hidden');
          if (window.load_more_btn) window.load_more_btn.style.display = 'flex';
        }
      };
      
      // Add click handler
      newBtn.addEventListener('click', function(e) {
        e.preventDefault();
        window.loadMoreProducts();
        return false;
      });
      
      // Replace button
      oldBtn.parentNode.replaceChild(newBtn, oldBtn);
      window.load_more_btn = newBtn;
    }, 100);
  }

  createSearchParams(form) {
    const formData = new FormData(form);
    return new URLSearchParams(formData).toString();
  }

  onSubmitForm(searchParams, event) {
    FacetFiltersForm.renderPage(searchParams, event);
  }

  onSubmitHandler(event) {
    event.preventDefault();

    const sortFilterForms = document.querySelectorAll('facet-filters-form form');
    
    if (event.srcElement.className == 'mobile-facets__checkbox') {
      const searchParams = this.createSearchParams(event.target.closest('form'));
      this.onSubmitForm(searchParams, event);
    } else {
      const forms = [];
      const isMobile = event.target.closest('form')?.id === 'FacetFiltersFormMobile';

      sortFilterForms.forEach((form) => {
        if (!isMobile) {
          if (form.id === 'FacetSortForm' || form.id === 'FacetFiltersForm' || form.id === 'FacetSortDrawerForm') {
            forms.push(this.createSearchParams(form));
          }
        } else if (form.id === 'FacetFiltersFormMobile') {
          forms.push(this.createSearchParams(form));
        }
      });
      
      this.onSubmitForm(forms.join('&'), event);
    }
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();
    const url =
      event.currentTarget.href.indexOf('?') == -1
        ? ''
        : event.currentTarget.href.slice(event.currentTarget.href.indexOf('?') + 1);
    FacetFiltersForm.renderPage(url);
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
FacetFiltersForm._warnedNoSection = false;
customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();

class PriceRange extends HTMLElement {
  constructor() {
    super();
    this.querySelectorAll('input').forEach((element) => {
      element.addEventListener('change', this.onRangeChange.bind(this));
      element.addEventListener('keydown', this.onKeyDown.bind(this));
    });
    this.setMinAndMaxValues();
  }

  onRangeChange(event) {
    this.adjustToValidValues(event.currentTarget);
    this.setMinAndMaxValues();
  }

  onKeyDown(event) {
    if (event.metaKey) return;
    const pattern = /[0-9]|\.|,|'| |Tab|Backspace|Enter|ArrowUp|ArrowDown|ArrowLeft|ArrowRight|Delete|Escape/;
    if (!event.key.match(pattern)) event.preventDefault();
  }

  setMinAndMaxValues() {
    const inputs = this.querySelectorAll('input');
    const minInput = inputs[0];
    const maxInput = inputs[1];
    if (maxInput.value) minInput.setAttribute('data-max', maxInput.value);
    if (minInput.value) maxInput.setAttribute('data-min', minInput.value);
    if (minInput.value === '') maxInput.setAttribute('data-min', 0);
    if (maxInput.value === '') minInput.setAttribute('data-max', maxInput.getAttribute('data-max'));
  }

  adjustToValidValues(input) {
    const value = Number(input.value);
    const min = Number(input.getAttribute('data-min'));
    const max = Number(input.getAttribute('data-max'));
    if (value < min) input.value = min;
    if (value > max) input.value = max;
  }
}
customElements.define('price-range', PriceRange);

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    const facetLink = this.querySelector('a');
    facetLink.setAttribute('role', 'button');
    facetLink.addEventListener('click', this.closeFilter.bind(this));
    facetLink.addEventListener('keyup', (event) => {
      event.preventDefault();
      if (event.code.toUpperCase() === 'SPACE') this.closeFilter(event);
    });
  }

  closeFilter(event) {
    event.preventDefault();
    const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
    form.onActiveFilterClick(event);
  }
}
customElements.define('facet-remove', FacetRemove);